import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Spin, message, Form } from "antd";
import {
  Loading3QuartersOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { RepayStepAssets } from "@dreambigger/shared/src/types";
import StepWrapper from "./step-wrapper";
import { StepProps } from "../pages/flows/[flowId]";
import { useApplication, useRepay } from "../api";
import { changeStepForward } from "../utils/changeStepForward";
import { useSegment } from "@dreambigger/shared/src/hooks";

const spinnerIcon = <Loading3QuartersOutlined spin />;

// Default polling configuration values
// Total max polling duration: ~90 seconds (sum of all delays)
const DEFAULT_POLLING_MAX_ATTEMPTS = 10;
// Exponential backoff delays in milliseconds:
// - Attempt 1: 0ms (immediate check)
// - Attempt 2: 1 second wait
// - Attempt 3: 2 seconds wait
// - Attempt 4: 4 seconds wait
// - Attempt 5: 8 seconds wait
// - Attempts 6-10: 15 seconds wait each
const DEFAULT_POLLING_DELAYS_MS = [0, 1000, 2000, 4000, 8000, 15000];

// The events returned from the Repay iframe
enum RepayMessageEvent {
  PAYMENT_ERROR = "PAYMENT_ERROR",
  PAYMENT_SUCCESS = "PAYMENT_SUCCESS",
}

/**
 * Payment statuses for tracking the payment flow UI state
 * Used internally to manage the user experience during payment processing
 */
enum PaymentStatus {
  /** Payment failed - user needs to retry */
  FAILED = "failed",
  /** Initial state - payment not yet initiated */
  PENDING = "pending",
  /** Payment is being processed - shows loading state */
  PROCESSING = "processing",
  /** Payment completed successfully - allows user to continue */
  SUCCESS = "success",
  /** Payment verification timed out - user can continue but payment may still be processing */
  TIMEOUT = "timeout",
}

/**
 * Payment verification statuses saved to application
 * Tracks the verification state of payments processed through Repay
 */
enum PaymentVerificationStatus {
  /** Payment was processed successfully but webhook confirmation is still pending */
  PENDING_WEBHOOK_VERIFICATION = "pending_webhook_verification",
  /** Payment is successful but webhook verification is disabled or skipped (meaning we have no record of it in our database - but it exists in Repay's) */
  UNVERIFIED = "unverified",
  /** Payment was verified through webhook confirmation sent from Repay */
  VERIFIED = "verified",
}

// Payment status messages
enum PaymentMessage {
  ALREADY_COMPLETED = "Payment already completed!",
  FAILED = "Payment failed. Please try again.",
  PROCESSING = "Finalizing transaction, please do not close this window...",
  SAVE_ERROR = "Error saving payment information. Please try again.",
  SUCCESS = "Payment successful! Please proceed.",
  TIMEOUT = "Payment verification timed out. You may continue or retry.",
  VERIFICATION_FAILED = "Payment verification failed. Please try again.",
}

// TODO: Fix a few final issues with frontend
export default function Repay({ flow, step, brand, progress }: StepProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [repayUrl, setRepayUrl] = useState<string | undefined>();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.PENDING
  );
  const [paymentMessage, setPaymentMessage] = useState<string>("");

  const applicationHelper = useApplication(flow.financialInstitution.id);
  const repayApi = useRepay(flow.financialInstitution.id);
  const segment = useSegment();

  const assets = step.assets as RepayStepAssets;
  const repayConfigurationUUID = assets?.repayConfigurationUUID;

  // Get polling configuration with defaults
  // Default behavior (pollingEnabled = false):
  // - Payment is immediately recorded as successful with reference "PAYMENT_RECORDED"
  // - No webhook verification polling occurs
  // - User can proceed immediately after payment
  // When polling is enabled (pollingConfig.enabled = true):
  // - System polls for webhook confirmation up to maxAttempts times
  // - Uses exponential backoff delays specified in intervalDelaysMs
  // - If polling exhausts, payment is still recorded as successful with "PENDING_WEBHOOK_VERIFICATION"
  const pollingEnabled = assets?.pollingConfig?.enabled ?? false;
  const pollingMaxAttempts =
    assets?.pollingConfig?.maxAttempts ?? DEFAULT_POLLING_MAX_ATTEMPTS;
  const pollingDelaysMs =
    assets?.pollingConfig?.intervalDelaysMs ?? DEFAULT_POLLING_DELAYS_MS;

  // Use ref to track if we're already polling
  const isPollingRef = useRef(false);

  // Use ref to prevent concurrent payment processing
  const isProcessingPaymentRef = useRef(false);

  // Track the amount used for the current repay URL
  const currentAmountRef = useRef<string | null>(null);

  // Track if payment was just completed (vs returning to page)
  const [justCompletedPayment, setJustCompletedPayment] = useState(false);

  // Get the application using useMemo to avoid re-fetching
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow.id]
  );

  // Get the amount from the configured step and field
  const amount = useMemo(() => {
    if (!application || !assets?.amountSlug || !assets?.amountFieldKey) {
      return null;
    }

    const amountResponse = applicationHelper.findResponseByApplicationId(
      application.id,
      assets.amountSlug
    );

    return (
      (amountResponse?.fields?.[assets.amountFieldKey] as string | number) ||
      null
    );
  }, [
    application,
    applicationHelper,
    assets?.amountSlug,
    assets?.amountFieldKey,
  ]);

  // Validate and format amount
  const validatedAmount = useMemo(() => {
    if (!amount) return null;

    // Convert to number and validate
    const numericAmount = Number(amount);

    if (isNaN(numericAmount)) {
      return null;
    }

    if (numericAmount <= 0) {
      return null;
    }

    return {
      numeric: numericAmount,
      string: numericAmount.toFixed(2),
      formatted: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numericAmount),
    };
  }, [amount]);

  // Check if payment was already completed (checking existing payment data in the application)
  const existingPaymentData = useMemo(() => {
    if (!application) return null;

    const response = applicationHelper.findResponseByApplicationId(
      application.id,
      step.slug
    );

    return response?.fields?.paymentReferenceNumber ||
      response?.fields?.paymentStatus
      ? response.fields
      : null;
  }, [application, applicationHelper, step.slug]);

  // Update payment status based on information from the application
  useEffect(() => {
    if (
      existingPaymentData?.paymentReferenceNumber ||
      existingPaymentData?.paymentStatus
    ) {
      // Payment already exists in DB
      if (!justCompletedPayment) {
        // User is returning to a previously completed payment
        setPaymentStatus(PaymentStatus.SUCCESS);
        setPaymentMessage(PaymentMessage.ALREADY_COMPLETED);
        setLoading(false);
      }
      // If justCompletedPayment is true, keep the current status/message
    } else {
      // No payment in DB - reset to initial state
      // Don't reset if we just completed payment (user needs to see receipt options)
      if (paymentStatus === PaymentStatus.SUCCESS && !justCompletedPayment) {
        // Payment was successful before but now cleared from DB
        setPaymentStatus(PaymentStatus.PENDING);
        setPaymentMessage("");
      } else if (
        paymentStatus === PaymentStatus.FAILED &&
        !isPollingRef.current
      ) {
        setPaymentStatus(PaymentStatus.PENDING);
        setPaymentMessage("");
      }
    }
  }, [
    existingPaymentData,
    justCompletedPayment,
    paymentStatus,
    paymentMessage,
  ]);

  // Handle messages from the Repay iframe
  const handleIframeMessage = useCallback(
    (event: MessageEvent) => {
      // Validate origin - only accept messages from the Repay iframe
      if (!repayUrl) {
        return;
      }

      try {
        const repayOrigin = new URL(repayUrl).origin;
        if (event.origin !== repayOrigin) {
          return;
        }
      } catch (error) {
        return;
      }

      const messageData = event.data;

      // Check for Repay payment events
      if (messageData === RepayMessageEvent.PAYMENT_SUCCESS) {
        // Prevent double-processing using ref for immediate effect
        if (isProcessingPaymentRef.current) {
          console.warn(
            "Payment already being processed, ignoring duplicate event"
          );
          return;
        }

        // Also check status for additional safety
        if (
          paymentStatus === PaymentStatus.PROCESSING ||
          paymentStatus === PaymentStatus.SUCCESS
        ) {
          console.warn(
            "Payment already in processing or success state, ignoring duplicate event"
          );
          return;
        }

        // Set processing flag immediately
        isProcessingPaymentRef.current = true;

        setPaymentStatus(PaymentStatus.PROCESSING);
        setPaymentMessage(PaymentMessage.PROCESSING);

        // Start polling for transaction data
        pollForTransactionRef.current?.();
      } else if (messageData === RepayMessageEvent.PAYMENT_ERROR) {
        // Show error message to user
        message.error(PaymentMessage.FAILED);

        // Reset processing flag to allow retry
        isProcessingPaymentRef.current = false;
      } else {
        console.info(
          "Received unknown message from Repay iframe:",
          messageData
        );
      }
    },
    [repayUrl, paymentStatus, pollingEnabled, amount]
  );

  // Handle successful payment completion
  const handlePaymentComplete = useCallback(
    async (transactionData: {
      paymentReferenceNumber: string;
      amount: string | number;
    }) => {
      if (!application) return;

      // Validate transaction data
      if (
        !transactionData?.paymentReferenceNumber ||
        !transactionData?.amount
      ) {
        console.error("Missing transaction id or amount in response:");

        message.error(
          "Payment verification failed. Missing transaction details."
        );

        setPaymentStatus(PaymentStatus.FAILED);
        setPaymentMessage(PaymentMessage.VERIFICATION_FAILED);

        isPollingRef.current = false;
        isProcessingPaymentRef.current = false;
        return;
      }

      // Mark that payment was just completed BEFORE saving to prevent UI switch
      setJustCompletedPayment(true);

      setPaymentStatus(PaymentStatus.SUCCESS);
      setPaymentMessage(PaymentMessage.SUCCESS);

      try {
        // Save payment data to application
        // Only store actual payment reference numbers, not placeholder values
        const fields: Record<string, any> = {
          paymentStatus:
            transactionData.paymentReferenceNumber === "PAYMENT_RECORDED"
              ? PaymentVerificationStatus.UNVERIFIED
              : transactionData.paymentReferenceNumber ===
                "PENDING_WEBHOOK_VERIFICATION"
              ? PaymentVerificationStatus.PENDING_WEBHOOK_VERIFICATION
              : PaymentVerificationStatus.VERIFIED,
        };

        // Only add paymentReferenceNumber if it's a real reference
        if (
          transactionData.paymentReferenceNumber &&
          transactionData.paymentReferenceNumber !== "PAYMENT_RECORDED" &&
          transactionData.paymentReferenceNumber !==
            "PENDING_WEBHOOK_VERIFICATION"
        ) {
          fields.paymentReferenceNumber =
            transactionData.paymentReferenceNumber;
        }

        const saved = await applicationHelper.update(application.id, {
          slug: step.slug,
          type: step.type,
          fields,
        });

        if (saved) {
          // Track payment success
          segment.track({
            action: "Payment Complete",
            label: `Repay - ${step.slug}`,
            properties: {
              paymentReferenceNumber: transactionData.paymentReferenceNumber,
              amount: transactionData.amount,
            },
          });
        }
      } catch (error) {
        message.error(PaymentMessage.SAVE_ERROR);
        setPaymentStatus(PaymentStatus.FAILED);
      } finally {
        isPollingRef.current = false;
        isProcessingPaymentRef.current = false;
      }
    },
    [application, applicationHelper, step, segment]
  );

  // Polling function
  const pollForTransaction = useCallback(async () => {
    if (!application || !amount || isPollingRef.current) return;

    isPollingRef.current = true;

    // If polling is disabled, immediately record as successful
    if (!pollingEnabled) {
      await handlePaymentComplete({
        paymentReferenceNumber: "PAYMENT_RECORDED",
        amount: amount || 0,
      });
      isPollingRef.current = false;
      return;
    }

    // Use configured delays or defaults
    const delays = pollingDelaysMs;

    for (let attempt = 0; attempt < pollingMaxAttempts; attempt++) {
      // Add delay before attempt (no delay on first attempt)
      if (attempt > 0) {
        const delay = delays[Math.min(attempt, delays.length - 1)];
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      try {
        // Use the getRepayPayment endpoint to check for payment
        const paymentData = await repayApi.getRepayPayment(application.id);

        if (paymentData && paymentData.paymentReference) {
          // Transform the response to match what handlePaymentComplete expects
          await handlePaymentComplete({
            paymentReferenceNumber: paymentData.paymentReference,
            amount: paymentData.amount,
          });
          return;
        }
      } catch (error) {
        console.error(`Polling attempt ${attempt + 1} failed`);
      }
    }

    // If we've exhausted all attempts, record payment as successful and allow user to continue
    console.warn(
      "Polling exhausted, recording payment as successful to allow user to continue"
    );

    // Record the payment as successful in the application
    try {
      await handlePaymentComplete({
        paymentReferenceNumber: "PENDING_WEBHOOK_VERIFICATION",
        amount: amount || 0,
      });
    } catch (error) {
      console.error("Failed to record payment after polling timeout:", error);

      // Even if recording fails, allow user to continue
      setPaymentStatus(PaymentStatus.TIMEOUT);
      setPaymentMessage(PaymentMessage.TIMEOUT);

      message.warning(
        "Payment verification timed out. You may continue with your application."
      );
    }

    isPollingRef.current = false;
    isProcessingPaymentRef.current = false;
    // repayApi is a stable reference, so we can safely use it here without causing infinite rerenders
  }, [
    application,
    amount,
    handlePaymentComplete,
    pollingDelaysMs,
    pollingMaxAttempts,
    pollingEnabled,
  ]);

  // Use ref to store the polling function
  const pollForTransactionRef = useRef<() => Promise<void>>();
  pollForTransactionRef.current = pollForTransaction;

  // Add event listener for iframe messages to handle payment events received from Repay
  useEffect(() => {
    window.addEventListener("message", handleIframeMessage);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [handleIframeMessage]);

  // Prevent user from leaving during payment processing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (paymentStatus === PaymentStatus.PROCESSING || isPollingRef.current) {
        e.preventDefault();
        return;
      }
    };

    // Add beforeunload event listener to warn user if they try to leave during payment processing
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [paymentStatus]);

  // Detect amount changes and reset URL if needed
  useEffect(() => {
    // If application is not loaded and we have a URL, clear it
    if (
      !application &&
      repayUrl &&
      !(
        existingPaymentData?.paymentReferenceNumber ||
        existingPaymentData?.paymentStatus
      )
    ) {
      setRepayUrl(undefined);
      setLoading(true); // Show loading while waiting for application
      setPaymentStatus(PaymentStatus.PENDING);
      setPaymentMessage("");
      currentAmountRef.current = null;
      return;
    }

    // If amount becomes invalid, clear the URL
    if (
      !validatedAmount &&
      repayUrl &&
      !(
        existingPaymentData?.paymentReferenceNumber ||
        existingPaymentData?.paymentStatus
      )
    ) {
      setRepayUrl(undefined);
      setLoading(false);
      setPaymentStatus(PaymentStatus.PENDING);
      setPaymentMessage("");
      currentAmountRef.current = null;
      return;
    }

    if (
      validatedAmount &&
      currentAmountRef.current &&
      currentAmountRef.current !== validatedAmount.string &&
      repayUrl &&
      !(
        existingPaymentData?.paymentReferenceNumber ||
        existingPaymentData?.paymentStatus
      )
    ) {
      // Prevent amount changes during loading or payment processing
      if (
        loading ||
        paymentStatus === PaymentStatus.PROCESSING ||
        isPollingRef.current
      ) {
        return;
      }

      // Clear the current URL to force regeneration
      setRepayUrl(undefined);
      setLoading(true);
      setPaymentStatus(PaymentStatus.PENDING);
      setPaymentMessage("");

      // Clear the current amount ref so initializeRepay will run again
      currentAmountRef.current = null;
    }
  }, [
    application,
    validatedAmount,
    repayUrl,
    existingPaymentData,
    loading,
    paymentStatus,
  ]);

  useEffect(() => {
    const initializeRepay = async () => {
      // Skip if payment already completed
      if (
        existingPaymentData?.paymentReferenceNumber ||
        existingPaymentData?.paymentStatus
      ) {
        return;
      }

      if (!repayConfigurationUUID) {
        console.error("Repay configuration UUID is missing");
        message.error("Payment configuration error. Please contact support.");
        setLoading(false);
        setRepayUrl(undefined); // Clear any existing iframe
        return;
      }

      if (!application) {
        // Application is still loading, wait for it
        return;
      }

      if (!assets?.amountSlug || !assets?.amountFieldKey) {
        console.error(
          "Amount configuration is missing in Repay step assets. amountSlug"
        );
        message.error("Payment configuration error. Please contact support.");
        setLoading(false);
        setRepayUrl(undefined); // Clear any existing iframe
        return;
      }

      if (!amount) {
        console.error(
          "Amount not found. amountSlug:",
          assets.amountSlug,
          "amountFieldKey:",
          assets.amountFieldKey
        );
        message.error(
          "Payment amount not found. Please complete all required steps."
        );
        setLoading(false);
        setRepayUrl(undefined); // Clear any existing iframe
        return;
      }

      // Validate amount before proceeding
      if (!validatedAmount) {
        console.error("Invalid payment amount:", amount);
        message.error(
          "Invalid payment amount. Please go back and enter a valid amount."
        );
        setLoading(false);
        setRepayUrl(undefined); // Clear any existing iframe
        return;
      }

      try {
        // Step 1: Get checkout form
        const checkoutFormResponse = await repayApi.getCheckoutForm({
          repayConfigurationUUID,
          onboardingApplicationId: application.id,
        });

        const formId = checkoutFormResponse.checkoutFormId;

        // Step 2: Create one-time-use URL
        const oneTimeUrlResponse = await repayApi.createOneTimeUseUrl({
          repayConfigurationUUID,
          onboardingApplicationId: application.id,
          checkoutFormId: formId,
          oneTimeUrlParams: {
            amount: validatedAmount.string, // Use validated amount with proper formatting
            // transaction_type and Source will come from the Repay configuration
          },
        });

        const { url } = oneTimeUrlResponse;
        setRepayUrl(url);

        // Store the amount that was used to generate this URL
        currentAmountRef.current = validatedAmount.string;
      } catch (error) {
        console.error("Error initializing Repay:", error);
        message.error(
          "Something went wrong retrieving payment form. Please contact support."
        );
      } finally {
        setLoading(false);
      }
    };

    initializeRepay();
  }, [
    repayConfigurationUUID,
    flow.id,
    flow.financialInstitution.id,
    application,
    amount,
    validatedAmount,
    assets?.amountSlug,
    assets?.amountFieldKey,
    existingPaymentData?.paymentReferenceNumber,
    existingPaymentData?.paymentStatus,
    // repayApi is excluded as it's a stable reference that doesn't change, otherwise it creates an infinite loop
  ]);

  // Auto-scroll to Continue button when payment just completed
  useEffect(() => {
    if (
      justCompletedPayment &&
      (paymentStatus === PaymentStatus.SUCCESS ||
        paymentStatus === PaymentStatus.TIMEOUT)
    ) {
      // Small delay to ensure UI has updated
      setTimeout(() => {
        // Scroll to bottom where Continue button is located
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [paymentStatus, justCompletedPayment]);

  // Handle form submission (when Continue button is clicked)
  const handleNext = useCallback(async () => {
    if (
      paymentStatus === PaymentStatus.SUCCESS ||
      paymentStatus === PaymentStatus.TIMEOUT
    ) {
      // Clear the justCompletedPayment flag when navigating away
      setJustCompletedPayment(false);
      changeStepForward(assets?.nextSlug, {});
    }
  }, [paymentStatus, assets?.nextSlug]);

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    handleNext,
    disabled:
      paymentStatus !== PaymentStatus.SUCCESS &&
      paymentStatus !== PaymentStatus.TIMEOUT,
    saving: paymentStatus === PaymentStatus.PROCESSING,
  };

  return (
    <StepWrapper {...wrapperProps}>
      {loading ? (
        <div className="tc pv-4">
          <Spin indicator={spinnerIcon} size="large" />
        </div>
      ) : (existingPaymentData?.paymentReferenceNumber ||
          existingPaymentData?.paymentStatus) &&
        !justCompletedPayment ? (
        // Show success message if returning to already completed payment
        <div className="tc pv-4">
          <CheckCircleFilled className="f-10 mb-4" style={{ color: "green" }} />
          <h2 className="f-6 fwb mb-2">Payment Already Completed</h2>
          {existingPaymentData.paymentReferenceNumber && (
            <p className="f-4 gray-8 mb-4">
              Payment Reference #:{" "}
              {String(existingPaymentData.paymentReferenceNumber)}
            </p>
          )}
        </div>
      ) : repayUrl ? (
        <div>
          {/* Show payment status if not pending */}
          {paymentStatus !== PaymentStatus.PENDING && (
            <div
              className={`mb-4 ph-4 pv-3 br-2 flex items-center ba ${
                paymentStatus === PaymentStatus.PROCESSING
                  ? "b-primary"
                  : paymentStatus === PaymentStatus.SUCCESS
                  ? "b-lightPrimary"
                  : "b-lightPrimary"
              }`}
              style={{
                backgroundColor: "white",
              }}
            >
              {paymentStatus === PaymentStatus.PROCESSING ? (
                <Spin indicator={spinnerIcon} className="mr-2" />
              ) : paymentStatus === PaymentStatus.SUCCESS ? (
                <CheckCircleFilled
                  className="f-5 mr-2"
                  style={{ color: "green" }}
                />
              ) : (
                <CloseCircleFilled
                  className="f-5 mr-2"
                  style={{ color: "#ef4444" }}
                />
              )}
              <span
                className={`f-4 gray-8 ${
                  paymentStatus === PaymentStatus.PROCESSING ||
                  paymentStatus === PaymentStatus.SUCCESS
                    ? "fwb"
                    : ""
                }`}
              >
                {paymentMessage}
              </span>
            </div>
          )}

          <iframe
            src={repayUrl}
            className="ba b-lightPrimary br-2"
            style={{
              width: "100%",
              height: "70vh",
            }}
            title="Repay Payment Form"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="tc pv-4">
          <CloseCircleFilled
            className="f-10 mb-3"
            style={{ color: "#ef4444" }}
          />
          <p className="f-4 fwb" style={{ color: "#ef4444" }}>
            Unable to load payment form. Please refresh the page and try again
            or contact support.
          </p>
        </div>
      )}
    </StepWrapper>
  );
}
